import React, { useState, useEffect, useMemo } from "react";
import { 
  ChevronLeft, 
  Edit3, 
  Eye, 
  Users, 
  PlayCircle, 
  CheckCircle2, 
  Clock, 
  TrendingUp, 
  Layout, 
  BookOpen,
  Plus,
  Layers,
  ChevronDown,
  ChevronUp,
  Video,
  FileText
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import Button from "../../components/common/Button";
import { 
  fetchCourseDetail, 
  fetchTrackDetail, 
  CourseData, 
  TrackData 
} from "../../services/curriculumService";
import { useQuery } from "@tanstack/react-query";
import { queryKeys } from "../../lib/queryClient";

const GlassCard = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <div className={`cht-bg-white/90 cht-backdrop-blur-xl cht-border cht-border-gray-100 cht-shadow-[0_20px_50px_rgba(0,0,0,0.04)] ${className}`}>
    {children}
  </div>
);

const InstructorCourseDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<"overview" | "curriculum">("overview");

  const isExplicitTrack = searchParams.get("type") === "track" || location.pathname.toLowerCase().includes("track");

  const { data: course = null, isLoading: loading } = useQuery({
    queryKey: isExplicitTrack
      ? queryKeys.curriculumAdmin.trackDetail(id || "")
      : queryKeys.curriculumAdmin.courseDetail(id || ""),
    queryFn: async () => {
      if (!id) return null;
      if (isExplicitTrack) {
        return fetchTrackDetail(id);
      }
      try {
        return await fetchCourseDetail(id);
      } catch {
        return await fetchTrackDetail(id);
      }
    },
    enabled: Boolean(id),
  });

  const isTrack = isExplicitTrack || (course ? "course_in_track" in course : false);

  const coursesInTrack = useMemo(() => {
    if (!course || !("course_in_track" in course) || !course.course_in_track) return [];
    return course.course_in_track;
  }, [course]);

  const modules = useMemo(() => {
    if (!course) return [];
    if ("course_in_track" in course && course.course_in_track) {
      return course.course_in_track.flatMap((c) => c.modules || []);
    }
    return (course as CourseData).modules || [];
  }, [course]);

  const totalLessons = useMemo(() => {
    return modules.reduce((acc, m) => acc + (m.lessons?.length || 0), 0) || (course?.lessons_count || 0);
  }, [modules, course]);

  const totalTasks = useMemo(() => {
    return modules.reduce((acc, m) => acc + ((m.exercise_module || (m as any).exercises)?.length || 0), 0);
  }, [modules]);

  const handlePreview = () => {
    if (!course) return;
    const slug = course.slug || course.id;
    if (isTrack) {
      navigate(`/instructor/dashboard/my-tracks/${slug}/preview?trackId=${course.id}`);
    } else {
      navigate(`/instructor/dashboard/my-courses/${slug}/preview?courseId=${course.id}`);
    }
  };

  const handleEdit = () => {
    if (!course) return;
    navigate(`/instructor/dashboard/my-courses/edit/${course.id}?type=${isTrack ? "track" : "course"}`);
  };

  if (loading) {
    return (
      <div className="cht-space-y-6 cht-p-8">
        <div className="cht-h-10 cht-w-1/3 cht-bg-slate-200 cht-rounded-xl cht-animate-pulse" />
        <div className="cht-h-64 cht-bg-slate-100 cht-rounded-3xl cht-animate-pulse" />
      </div>
    );
  }

  if (!course) {
    return (
      <div className="cht-p-12 cht-text-center cht-bg-white cht-rounded-3xl cht-border cht-border-gray-100">
        <h3 className="cht-text-lg cht-font-black cht-text-slate-800">Curriculum Not Found</h3>
        <p className="cht-text-xs cht-text-slate-400 cht-mt-1">The requested course or track does not exist.</p>
        <Button variant="primary" size="sm" className="cht-mt-4 cht-rounded-xl" onClick={() => navigate("/instructor/dashboard/my-courses")}>
          Return to My Courses
        </Button>
      </div>
    );
  }

  return (
    <div className="cht-space-y-8 cht-animate-in cht-fade-in cht-duration-500">
      {/* Header */}
      <header className="cht-flex cht-flex-col md:cht-flex-row cht-items-start md:cht-items-center cht-justify-between cht-gap-6">
        <div className="cht-flex cht-items-center cht-gap-4">
          <button 
            type="button"
            onClick={() => navigate("/instructor/dashboard/my-courses")}
            className="cht-w-10 cht-h-10 cht-flex cht-items-center cht-justify-center cht-bg-white cht-rounded-full cht-border cht-border-gray-200 hover:cht-bg-gray-50 transition-colors"
          >
            <ChevronLeft size={20} className="cht-text-gray-600" />
          </button>
          <div>
            <div className="cht-flex cht-items-center cht-gap-2.5 cht-mb-1">
              <span className="cht-px-2.5 cht-py-0.5 cht-rounded-full cht-text-[9px] cht-font-black cht-uppercase cht-tracking-widest cht-bg-emerald-50 cht-text-emerald-600">
                Published
              </span>
              <span className="cht-text-[10px] cht-font-black cht-text-slate-400 cht-uppercase cht-tracking-widest">
                {isTrack ? "Career Track" : "Standard Course"}
              </span>
            </div>
            <h1 className="cht-text-2xl md:cht-text-3xl cht-font-black cht-text-[#1e293b] cht-tracking-tight !cht-font-heading">
              {course.title}
            </h1>
          </div>
        </div>

        <div className="cht-flex cht-items-center cht-gap-3">
          <Button 
            variant="outline" 
            leftIcon={<Eye size={16} />} 
            className="cht-rounded-xl"
            onClick={handlePreview}
          >
            Preview Student View
          </Button>
          <Button 
            variant="primary" 
            leftIcon={<Edit3 size={16} />} 
            className="cht-rounded-xl" 
            onClick={handleEdit}
          >
            Manage Curriculum
          </Button>
        </div>
      </header>

      {/* Tabs */}
      <div className="cht-flex cht-items-center cht-gap-8 cht-border-b cht-border-gray-100 cht-pb-0">
        {[
          { key: "overview", label: "Overview" },
          { key: "curriculum", label: `Curriculum (${modules.length} Modules)` }
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key as any)}
            className={`cht-pb-3 cht-text-xs cht-font-black cht-uppercase cht-tracking-[0.15em] cht-transition-all cht-relative ${
              activeTab === tab.key ? "cht-text-[#3e57da]" : "cht-text-gray-400 hover:cht-text-gray-600"
            }`}
          >
            {tab.label}
            {activeTab === tab.key && (
              <motion.div 
                layoutId="activeTabUnderline"
                className="cht-absolute cht-bottom-0 cht-left-0 cht-right-0 cht-h-1 cht-bg-[#3e57da] cht-rounded-t-full"
              />
            )}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        {activeTab === "overview" && (
          <motion.div 
            key="overview"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="cht-space-y-6"
          >
            {/* Quick Stats Matrix */}
            <div className="cht-grid cht-grid-cols-2 md:cht-grid-cols-4 cht-gap-4">
              <GlassCard className="cht-p-5 cht-rounded-[22px]">
                <div className="cht-w-9 cht-h-9 cht-rounded-xl cht-flex cht-items-center cht-justify-center cht-mb-3 cht-bg-blue-50 cht-text-[#3e57da]">
                  <Users size={18} />
                </div>
                <p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">Enrolled</p>
                <h4 className="cht-text-xl cht-font-black cht-text-gray-900 cht-mt-0.5">{course.enrolled_count || "0"}</h4>
              </GlassCard>

              <GlassCard className="cht-p-5 cht-rounded-[22px]">
                <div className="cht-w-9 cht-h-9 cht-rounded-xl cht-flex cht-items-center cht-justify-center cht-mb-3 cht-bg-purple-50 cht-text-purple-600">
                  <PlayCircle size={18} />
                </div>
                <p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">Lessons</p>
                <h4 className="cht-text-xl cht-font-black cht-text-gray-900 cht-mt-0.5">{totalLessons}</h4>
              </GlassCard>

              <GlassCard className="cht-p-5 cht-rounded-[22px]">
                <div className="cht-w-9 cht-h-9 cht-rounded-xl cht-flex cht-items-center cht-justify-center cht-mb-3 cht-bg-emerald-50 cht-text-emerald-600">
                  <CheckCircle2 size={18} />
                </div>
                <p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">Tasks</p>
                <h4 className="cht-text-xl cht-font-black cht-text-gray-900 cht-mt-0.5">{totalTasks}</h4>
              </GlassCard>

              <GlassCard className="cht-p-5 cht-rounded-[22px]">
                <div className="cht-w-9 cht-h-9 cht-rounded-xl cht-flex cht-items-center cht-justify-center cht-mb-3 cht-bg-amber-50 cht-text-amber-600">
                  <Clock size={18} />
                </div>
                <p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">Duration</p>
                <h4 className="cht-text-xl cht-font-black cht-text-gray-900 cht-mt-0.5">{course.duration || "Self-Paced"}</h4>
              </GlassCard>
            </div>

            {/* Main Overview Body */}
            <div className="cht-grid cht-grid-cols-1 lg:cht-grid-cols-12 cht-gap-6">
              <GlassCard className="lg:cht-col-span-8 cht-p-8 cht-rounded-[28px] cht-space-y-6">
                <div>
                  <h3 className="cht-text-xs cht-font-black cht-text-slate-400 cht-uppercase cht-tracking-widest cht-mb-2">
                    Description & Objectives
                  </h3>
                  <div className="cht-text-sm cht-text-slate-700 cht-leading-relaxed cht-space-y-3">
                    {course.description || "Comprehensive syllabus covering practical workflows, code implementations, and project milestones."}
                  </div>
                </div>

                {course.requirement && (
                  <div className="cht-pt-4 cht-border-t cht-border-gray-100">
                    <h4 className="cht-text-xs cht-font-black cht-text-slate-400 cht-uppercase cht-tracking-widest cht-mb-2">
                      Requirements & Prerequisites
                    </h4>
                    <p className="cht-text-sm cht-text-slate-600">{course.requirement}</p>
                  </div>
                )}
              </GlassCard>

              <GlassCard className="lg:cht-col-span-4 cht-p-8 cht-rounded-[28px] cht-space-y-5">
                <h3 className="cht-text-xs cht-font-black cht-text-slate-400 cht-uppercase cht-tracking-widest">
                  Tuition & Tiering
                </h3>

                <div className="cht-p-4 cht-rounded-xl cht-bg-slate-50 cht-space-y-1">
                  <p className="cht-text-[10px] cht-font-bold cht-text-slate-400 cht-uppercase">Online Access</p>
                  <p className="cht-text-lg cht-font-bold cht-text-slate-900">₦{Number(course.online_price || 0).toLocaleString()}</p>
                </div>

                <div className="cht-p-4 cht-rounded-xl cht-bg-slate-50 cht-space-y-1">
                  <p className="cht-text-[10px] cht-font-bold cht-text-slate-400 cht-uppercase">Onsite Mentorship</p>
                  <p className="cht-text-lg cht-font-bold cht-text-slate-900">₦{Number(course.onsite_price || 0).toLocaleString()}</p>
                </div>

                <Button 
                  variant="primary" 
                  size="sm" 
                  className="cht-w-full cht-rounded-xl"
                  onClick={handleEdit}
                >
                  Edit Pricing & Settings
                </Button>
              </GlassCard>
            </div>
          </motion.div>
        )}

        {activeTab === "curriculum" && (
          <motion.div 
            key="curriculum"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="cht-space-y-6"
          >
            <div className="cht-flex cht-items-center cht-justify-between">
              <div>
                <h3 className="cht-text-xs cht-font-black cht-text-slate-400 cht-uppercase cht-tracking-widest">
                  {isTrack && coursesInTrack.length > 0
                    ? `Track Course Phases & Modules (${coursesInTrack.length} Phases)` 
                    : `Modules & Content Breakdown (${modules.length})`}
                </h3>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                className="cht-rounded-xl" 
                leftIcon={<Plus size={14} />} 
                onClick={handleEdit}
              >
                Add or Edit Curriculum
              </Button>
            </div>
            
            {isTrack && coursesInTrack.length > 0 ? (
              <div className="cht-space-y-8">
                {coursesInTrack.map((trackCourse, cIdx) => {
                  const cMods = trackCourse.modules || [];
                  const cLessons = cMods.reduce((acc, m) => acc + (m.lessons?.length || 0), 0);
                  const cTasks = cMods.reduce((acc, m) => acc + ((m.exercise_module || (m as any).exercises)?.length || 0), 0);

                  return (
                    <div key={trackCourse.id || cIdx} className="cht-space-y-3">
                      {/* Phase Header */}
                      <div className="cht-p-4 cht-rounded-2xl cht-bg-slate-50 cht-border cht-border-slate-200/80 cht-flex cht-items-center cht-justify-between">
                        <div className="cht-flex cht-items-center cht-gap-3">
                          <span className="cht-px-2.5 cht-py-1 cht-rounded-lg cht-bg-[#3e57da] cht-text-white cht-text-xs cht-font-black cht-uppercase cht-tracking-wider">
                            Phase 0{cIdx + 1}
                          </span>
                          <div>
                            <h4 className="cht-text-sm sm:cht-text-base cht-font-bold cht-text-slate-900 !cht-font-heading">
                              {trackCourse.title}
                            </h4>
                            <p className="cht-text-[11px] cht-text-slate-400 cht-font-medium">
                              {cMods.length} {cMods.length === 1 ? "module" : "modules"} • {cLessons} topics • {cTasks} tasks • {trackCourse.duration || "Self-paced"}
                            </p>
                          </div>
                        </div>

                        <Button
                          size="sm"
                          variant="ghost"
                          className="cht-text-xs cht-text-[#3e57da]"
                          onClick={handleEdit}
                        >
                          Manage Phase
                        </Button>
                      </div>

                      {/* Phase Modules */}
                      <div className="cht-space-y-3 cht-pl-0 sm:cht-pl-3">
                        {cMods.map((mod, idx) => (
                          <GlassCard key={mod.id || idx} className="cht-p-5 cht-rounded-2xl cht-space-y-3">
                            <div className="cht-flex cht-items-center cht-justify-between">
                              <div className="cht-flex cht-items-center cht-gap-3">
                                <div className="cht-w-8 cht-h-8 cht-bg-blue-50 cht-text-[#3e57da] cht-rounded-lg cht-flex cht-items-center cht-justify-center">
                                  <Layout size={16} />
                                </div>
                                <div>
                                  <h5 className="cht-text-sm cht-font-bold cht-text-slate-900">
                                    Module {idx + 1}: {mod.title}
                                  </h5>
                                  <p className="cht-text-[10px] cht-text-slate-400 cht-uppercase cht-font-bold cht-tracking-wider">
                                    {(mod.lessons || []).length} Topics • {((mod.exercise_module || (mod as any).exercises) || []).length} Tasks
                                  </p>
                                </div>
                              </div>
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                className="cht-text-xs cht-text-[#3e57da]" 
                                onClick={handleEdit}
                              >
                                Edit
                              </Button>
                            </div>

                            {/* Lessons preview */}
                            {(mod.lessons || []).length > 0 && (
                              <div className="cht-pl-11 cht-space-y-1.5 cht-pt-2 cht-border-t cht-border-gray-50">
                                {(mod.lessons || []).map((l, lIdx) => {
                                  const atts = Array.isArray(l.attachments) ? l.attachments : [];
                                  const hasVideo = l.video_url || atts.some(a => a.type === "youtube" || a.type === "video");
                                  const attCount = atts.length;
                                  return (
                                    <div key={l.id || lIdx} className="cht-flex cht-items-center cht-justify-between cht-text-xs cht-text-slate-600">
                                      <span className="cht-font-medium cht-flex cht-items-center cht-gap-1.5">
                                        {hasVideo ? <Video size={12} className="cht-text-rose-500" /> : <FileText size={12} className="cht-text-slate-400" />}
                                        {l.title}
                                        {attCount > 0 && (
                                          <span className="cht-text-[9px] cht-bg-indigo-50 cht-text-[#3e57da] cht-px-1.5 cht-py-0.2 cht-rounded-full cht-font-bold">
                                            {attCount} {attCount === 1 ? "resource" : "resources"}
                                          </span>
                                        )}
                                      </span>
                                      <span className="cht-text-[10px] cht-text-slate-400">{l.duration}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </GlassCard>
                        ))}

                        {cMods.length === 0 && (
                          <div className="cht-py-8 cht-text-center cht-bg-white cht-rounded-2xl cht-border cht-border-dashed cht-border-gray-200">
                            <p className="cht-text-xs cht-text-slate-400">No modules in Phase 0{cIdx + 1} yet.</p>
                            <Button variant="primary" size="sm" className="cht-mt-2.5 cht-rounded-xl" onClick={handleEdit}>
                              Add First Module to Phase 0{cIdx + 1}
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              /* Standalone Course Modules List */
              <div className="cht-space-y-4">
                {modules.map((mod, idx) => (
                  <GlassCard key={mod.id || idx} className="cht-p-5 cht-rounded-2xl cht-space-y-3">
                    <div className="cht-flex cht-items-center cht-justify-between">
                      <div className="cht-flex cht-items-center cht-gap-3">
                        <div className="cht-w-8 cht-h-8 cht-bg-blue-50 cht-text-[#3e57da] cht-rounded-lg cht-flex cht-items-center cht-justify-center">
                          <Layout size={16} />
                        </div>
                        <div>
                          <h4 className="cht-text-sm cht-font-bold cht-text-slate-900">
                            Module {idx + 1}: {mod.title}
                          </h4>
                          <p className="cht-text-[10px] cht-text-slate-400 cht-uppercase cht-font-bold cht-tracking-wider">
                            {(mod.lessons || []).length} Topics • {((mod.exercise_module || (mod as any).exercises) || []).length} Tasks
                          </p>
                        </div>
                      </div>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="cht-text-xs cht-text-[#3e57da]" 
                        onClick={handleEdit}
                      >
                        Manage
                      </Button>
                    </div>

                    {(mod.lessons || []).length > 0 && (
                      <div className="cht-pl-11 cht-space-y-1.5 cht-pt-2 cht-border-t cht-border-gray-50">
                        {(mod.lessons || []).map((l, lIdx) => {
                          const atts = Array.isArray(l.attachments) ? l.attachments : [];
                          const hasVideo = l.video_url || atts.some(a => a.type === "youtube" || a.type === "video");
                          const attCount = atts.length;
                          return (
                            <div key={l.id || lIdx} className="cht-flex cht-items-center cht-justify-between cht-text-xs cht-text-slate-600">
                              <span className="cht-font-medium cht-flex cht-items-center cht-gap-1.5">
                                {hasVideo ? <Video size={12} className="cht-text-rose-500" /> : <FileText size={12} className="cht-text-slate-400" />}
                                {l.title}
                                {attCount > 0 && (
                                  <span className="cht-text-[9px] cht-bg-indigo-50 cht-text-[#3e57da] cht-px-1.5 cht-py-0.2 cht-rounded-full cht-font-bold">
                                    {attCount} {attCount === 1 ? "resource" : "resources"}
                                  </span>
                                )}
                              </span>
                              <span className="cht-text-[10px] cht-text-slate-400">{l.duration}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </GlassCard>
                ))}

                {modules.length === 0 && (
                  <div className="cht-py-12 cht-text-center cht-bg-white cht-rounded-2xl cht-border cht-border-dashed cht-border-gray-200">
                    <p className="cht-text-xs cht-text-slate-400">No modules created yet.</p>
                    <Button variant="primary" size="sm" className="cht-mt-3 cht-rounded-xl" onClick={handleEdit}>
                      Build Curriculum Modules
                    </Button>
                  </div>
                )}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default InstructorCourseDetail;
